import { useEffect, useRef, useState } from "react";

import {
  useFetchRecipientAddressRenameFields,
  useFetchSwiftAdditionalDetails,
  useFetchSwiftAdditionalFieldsByCountry,
} from "../../entities/recipients/requests";

import type { SwiftDestinationFormType } from "../../entities/recipients/forms";
import type { UseFormReturn } from "react-hook-form";

export type SwiftAddressField = "address" | "branch_address";

export interface SwiftInstitution {
  name: string;
  location: string;
}

// what the beneficiary bank's BIC (or, with a correspondent bank, its country) asks for
export interface BeneficiaryBankDetails {
  additionalFields: API.SwiftDestinationAdditionalField[];
  // the bank's own names for fields, e.g. account_identifier is "IBAN"
  renameFields: Record<string, string>;
  institution: SwiftInstitution | null;
}

type RenameFields = Record<string, string> | null;

const EMPTY_BANK_DETAILS: BeneficiaryBankDetails = {
  additionalFields: [],
  renameFields: {},
  institution: null,
};

const EMPTY_ADDRESS = {
  country_code: "",
  street_address: "",
  street_address_2: "",
  city: "",
  region_code: "",
  postal_code: "",
};

// the request shows its own error under the field; a failed lookup just finds nothing
const lookUp = async <T>(request: Promise<T> | Promise<void>) => {
  try {
    return await request;
  } catch {
    return undefined;
  }
};

interface WaitingRequest<T> {
  arg: string;
  isWanted: () => boolean;
  resolve: (res: T | undefined) => void;
}

/**
 * Sends a request hook's requests one at a time. The shared request hook
 * skips a request made while its rendered `loading` is true, which lasts until
 * the render after a request settles, so a newer call waits until then. Only
 * the newest waiting call is sent; one replaced, or no longer wanted when its
 * turn comes, resolves with nothing.
 */
const useQueuedRequest = <T>(
  request: (arg: string) => Promise<T> | Promise<void>,
  loading: boolean,
) => {
  // what the request hook's own guard sees: `loading` as of the last render
  const renderedLoading = useRef(loading);
  const running = useRef(false);
  const settled = useRef(false);
  const waiting = useRef<WaitingRequest<T> | null>(null);

  // sends the waiting call once the request hook is free
  const sendNext = () => {
    const call = waiting.current;
    if (running.current || !call) return;
    waiting.current = null;
    if (!call.isWanted()) {
      call.resolve(undefined);
      return;
    }
    running.current = true;
    settled.current = false;
    lookUp<T>(request(call.arg)).then((res) => {
      settled.current = true;
      call.resolve((res || undefined) as T | undefined);
      // a request that never rendered as loading leaves nothing to wait for
      if (!renderedLoading.current) {
        running.current = false;
        sendNext();
      }
    });
  };

  // declared after the request hook, so this runs once its guard has updated
  useEffect(() => {
    renderedLoading.current = loading;
    if (!loading && running.current && settled.current) {
      running.current = false;
      sendNext();
    }
    // only as loading changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (arg: string, isWanted: () => boolean) =>
    new Promise<T | undefined>((resolve) => {
      // a call still waiting is replaced by the newer one
      waiting.current?.resolve(undefined);
      waiting.current = { arg, isWanted, resolve };
      sendNext();
    });
};

const toInstitution = (
  res: API.SwiftAdditionalDetails,
): SwiftInstitution | null =>
  res.institution_name
    ? { name: res.institution_name, location: res.institution_location }
    : null;

/**
 * Looks up what an international wire destination needs as the user fills
 * the form in, and keeps the form's fields in step with it: the beneficiary
 * bank's BIC decides its extra fields, the correspondent bank's BIC is shown
 * with its institution, and each address's country decides its field labels.
 */
export const useInternationalWireLookups = (
  form: Pick<
    UseFormReturn<SwiftDestinationFormType>,
    "getValues" | "reset" | "setValue" | "trigger" | "watch"
  >,
  savedDestination?: API.SwiftDestination | null,
) => {
  const { getValues, reset, setValue, trigger, watch } = form;
  const beneficiaryBic = useFetchSwiftAdditionalDetails();
  const correspondentBic = useFetchSwiftAdditionalDetails();
  // one request per address, so a failed lookup only shows on its own address
  const addressRenames = {
    address: useFetchRecipientAddressRenameFields(),
    branch_address: useFetchRecipientAddressRenameFields(),
  };
  const countryFields = useFetchSwiftAdditionalFieldsByCountry();
  // declared after the request hooks, so their effects run first
  const requestBeneficiaryBic = useQueuedRequest(
    beneficiaryBic.fetchSwiftAdditionalDetails,
    beneficiaryBic.loading,
  );
  const requestCorrespondentBic = useQueuedRequest(
    correspondentBic.fetchSwiftAdditionalDetails,
    correspondentBic.loading,
  );
  const requestCountryFields = useQueuedRequest(
    countryFields.fetchSwiftAdditionalFieldsByCountry,
    countryFields.loading,
  );
  const requestAddressRenames = {
    address: useQueuedRequest(
      addressRenames.address.fetchRecipientAddressRenameFields,
      addressRenames.address.loading,
    ),
    branch_address: useQueuedRequest(
      addressRenames.branch_address.fetchRecipientAddressRenameFields,
      addressRenames.branch_address.loading,
    ),
  };

  const [beneficiaryBank, setBeneficiaryBank] =
    useState<BeneficiaryBankDetails>(EMPTY_BANK_DETAILS);
  const [correspondentInstitution, setCorrespondentInstitution] =
    useState<SwiftInstitution | null>(null);
  const [addressRenameFields, setAddressRenameFields] = useState<
    Record<SwiftAddressField, RenameFields>
  >({ address: null, branch_address: null });

  const isCorrespondentBankEnabled = watch("isCorrespondentBankEnabled");

  // the newest lookup of each kind; a response that isn't the newest, or comes
  // after the screen closes, mustn't write to a form that outlives the screen
  const latestLookups = useRef<Record<string, number>>({});
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // starts a lookup of this kind, and returns whether it's still the one to apply
  const startLookup = (kind: string) => {
    const id = (latestLookups.current[kind] ?? 0) + 1;
    latestLookups.current[kind] = id;
    return () => isMountedRef.current && latestLookups.current[kind] === id;
  };
  // the BIC the bank fields were filled in for, so looking the same BIC up again keeps them
  const bankFieldsBicRef = useRef(savedDestination?.bic || "");

  // replaces the form's values but not its defaults, so the change still counts as unsaved input
  const replaceValues = (values: SwiftDestinationFormType) =>
    reset(values, { keepDefaultValues: true });

  // clears what the last bank asked for, keeping the IBAN flag and any preserved fields
  const resetBankFields = (preserveFields: string[] = []) => {
    const resetValues: Record<string, unknown> = {
      renameFields: {},
      additionalFieldsToShow: {},
      account_identifier: "",
      branch_address: undefined,
      metadata: {
        iban: getValues("metadata.iban"),
        routing_number: "",
        phone: "",
        itin: "",
        bank_id: "",
        bank_name: "",
      },
    };
    preserveFields.forEach((field) => {
      delete resetValues[field];
    });
    replaceValues({ ...getValues(), ...resetValues });
  };

  const showBankFields = (
    additionalFields: API.SwiftDestinationAdditionalField[],
    renameFields: Record<string, string> | null,
    institution: SwiftInstitution | null,
  ) => {
    setBeneficiaryBank({
      additionalFields,
      renameFields: renameFields || {},
      institution,
    });
    setValue("renameFields", renameFields || {});
    setValue(
      "additionalFieldsToShow",
      Object.fromEntries(additionalFields.map((field) => [field.name, true])),
    );
  };

  // fills in what the BIC lookup already knows, without overwriting the user's input
  const prefillBankFields = (res: API.SwiftAdditionalDetails) => {
    const fieldNames = new Set(res.additional_fields.map((f) => f.name));
    if (
      fieldNames.has("branch_address") &&
      res.address &&
      !getValues("branch_address")
    ) {
      setValue("branch_address", res.address);
    }
    if (
      fieldNames.has("routing_number") &&
      res.swift_routing_code &&
      !getValues("metadata.routing_number")
    ) {
      setValue("metadata.routing_number", res.swift_routing_code);
    }
  };

  const applyBicDetails = (res: API.SwiftAdditionalDetails) => {
    showBankFields(
      res.additional_fields,
      res.rename_fields,
      toInstitution(res),
    );
    prefillBankFields(res);
  };

  // whether the bank was found, so its fields are known
  const lookUpBeneficiaryBic = async (bic: string) => {
    const isCurrent = startLookup("beneficiaryBank");
    setBeneficiaryBank((current) => ({ ...current, institution: null }));
    const res = await requestBeneficiaryBic(bic, isCurrent);
    if (!isCurrent() || !res?.additional_fields) return false;
    if (bic !== bankFieldsBicRef.current) resetBankFields();
    bankFieldsBicRef.current = bic;
    applyBicDetails(res);
    return true;
  };

  // whether the country's bank fields were found, so they're known
  const lookUpCountryFields = async (countryCode: string) => {
    const isCurrent = startLookup("beneficiaryBank");
    const res = await requestCountryFields(countryCode, isCurrent);
    if (!isCurrent() || !res?.additional_fields) return false;
    showBankFields(res.additional_fields, res.rename_fields, null);
    return true;
  };

  // whether the correspondent bank was found
  const lookUpCorrespondentBic = async (bic: string) => {
    const isCurrent = startLookup("correspondentBank");
    const res = await requestCorrespondentBic(bic, isCurrent);
    if (!isCurrent()) return false;
    setCorrespondentInstitution(res ? toInstitution(res) : null);
    return Boolean(res);
  };

  const lookUpAddressRenames = async (
    fieldName: SwiftAddressField,
    countryCode: string,
  ) => {
    const isCurrent = startLookup(fieldName);
    // a failed lookup clears the labels too, so the last country's don't linger
    const res = countryCode
      ? await requestAddressRenames[fieldName](countryCode, isCurrent)
      : null;
    if (!isCurrent()) return;
    setAddressRenameFields((current) => ({
      ...current,
      [fieldName]: res || null,
    }));
  };

  const clearBankFields = () => {
    // a lookup still running mustn't bring the last bank back
    startLookup("beneficiaryBank");
    bankFieldsBicRef.current = "";
    resetBankFields();
    setBeneficiaryBank(EMPTY_BANK_DETAILS);
  };

  // resolves whether the beneficiary bank's fields are known, so a save can wait for them
  const onBeneficiaryBicBlur = async (bic: string) => {
    // with a correspondent bank the beneficiary bank has no BIC to look up
    if (isCorrespondentBankEnabled) return true;
    if (bic) return lookUpBeneficiaryBic(bic);
    clearBankFields();
    return false;
  };

  // resolves whether the correspondent bank was found, so a save can wait for it
  const onCorrespondentBicBlur = async (bic: string) => {
    setCorrespondentInstitution(null);
    return bic ? lookUpCorrespondentBic(bic) : false;
  };

  // looks the beneficiary bank's country up again without clearing its fields,
  // e.g. when a save finds its last lookup failed; resolves whether they're known
  const lookUpBeneficiaryBankCountry = (countryCode: string) =>
    countryCode ? lookUpCountryFields(countryCode) : Promise.resolve(false);

  // resolves whether the bank's fields are known, so a save can wait for them
  const onCountryChange = async (
    fieldName: SwiftAddressField,
    countryCode: string,
  ) => {
    lookUpAddressRenames(fieldName, countryCode);
    // only with a correspondent bank does the beneficiary bank's country decide its fields;
    // without one, the branch address is a field the BIC asked for
    if (fieldName !== "branch_address" || !isCorrespondentBankEnabled) {
      return true;
    }
    // keep the country just picked
    resetBankFields(["branch_address"]);
    setBeneficiaryBank(EMPTY_BANK_DETAILS);
    return countryCode ? lookUpCountryFields(countryCode) : false;
  };

  const onAddressAutocomplete = (
    fieldName: SwiftAddressField,
    update: Partial<API.Address>,
  ) => {
    const previousCountry = getValues(fieldName)?.country_code;
    replaceValues({
      ...getValues(),
      [fieldName]: { ...getValues(fieldName), ...update },
    });
    // autocomplete can change the country; treat it like picking one from the dropdown
    if (update.country_code && update.country_code !== previousCountry) {
      onCountryChange(fieldName, update.country_code);
    }
  };

  const onCorrespondentBankToggle = (isActive: boolean) => {
    if (isActive) {
      // the beneficiary BIC's lookup no longer applies
      startLookup("beneficiaryBank");
      setValue("bic", "");
      bankFieldsBicRef.current = "";
      resetBankFields(["branch_address"]);
      // validation needs a branch address to check, not undefined
      if (!getValues("branch_address")) {
        setValue("branch_address", EMPTY_ADDRESS);
      }
      setBeneficiaryBank(EMPTY_BANK_DETAILS);
      return;
    }
    startLookup("correspondentBank");
    setValue("correspondent_bank_bic", "");
    setValue("metadata.bank_id", "");
    setValue("metadata.bank_name", "");
    setCorrespondentInstitution(null);
    const bic = getValues("bic");
    if (bic) {
      lookUpBeneficiaryBic(bic);
    } else {
      clearBankFields();
    }
  };

  // a saved destination's banks are looked up again to show their fields
  useEffect(() => {
    if (!savedDestination) return;
    bankFieldsBicRef.current = savedDestination.bic || "";
    const lookUpSaved = async () => {
      const savedCorrespondentBic = savedDestination.correspondent_bank_bic;
      if (savedCorrespondentBic) {
        const countryCode = savedDestination.branch_address?.country_code;
        await Promise.all([
          countryCode ? lookUpCountryFields(countryCode) : Promise.resolve(),
          lookUpCorrespondentBic(savedCorrespondentBic),
        ]);
      } else if (savedDestination.bic) {
        const isCurrent = startLookup("beneficiaryBank");
        const res = await requestBeneficiaryBic(
          savedDestination.bic,
          isCurrent,
        );
        if (isCurrent() && res?.additional_fields) applyBicDetails(res);
      }
      // point out anything the saved details are missing
      if (isMountedRef.current) await trigger();
    };
    lookUpSaved();
    // only when the saved destination changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedDestination?.id]);

  return {
    isCorrespondentBankEnabled,
    beneficiaryBank,
    isLoadingBeneficiaryBank: beneficiaryBic.loading,
    beneficiaryBicError: beneficiaryBic.errorMessage,
    correspondentInstitution,
    isLoadingCorrespondentBank: correspondentBic.loading,
    correspondentBicError: correspondentBic.errorMessage,
    // with a correspondent bank, looking up the beneficiary bank's country failed
    beneficiaryBankCountryError: countryFields.errorMessage,
    addressRenameFields,
    addressRenameErrors: {
      address: addressRenames.address.errorMessage,
      branch_address: addressRenames.branch_address.errorMessage,
    },
    onBeneficiaryBicBlur,
    onCorrespondentBicBlur,
    onCountryChange,
    lookUpBeneficiaryBankCountry,
    onAddressAutocomplete,
    onCorrespondentBankToggle,
  };
};
